import {Injectable} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors} from '@angular/forms';
import {
  FieldBlock,
  FormBlock,
  FormDefinition,
  GroupBlock,
  ListBlock
} from '../models/form-schema.model';
import {resolveDefault} from './default-value';
import {ValidatorMapperService} from './validator-mapper.service';
import {RulesEngineService} from './rules-engine.service';

@Injectable({
  providedIn: 'root'
})
export class FormFactoryService {
  constructor(
    private validatorMapper: ValidatorMapperService,
    private rulesEngine: RulesEngineService
  ) {
  }

  buildForm(blocks: FormBlock[]): FormGroup {
    const group = new FormGroup({});
    this.addBlocksToGroup(blocks, group);
    return group;
  }

  buildFormFromDefinition(definition: FormDefinition): FormGroup {
    const group = new FormGroup({});
    definition.pages.forEach(page => {
      this.addBlocksToGroup(page.blocks, group);
    });

    const validations = definition.validations ?? [];
    if (validations.length > 0) {
      group.addValidators((control: AbstractControl): ValidationErrors | null => {
        const value = (control as FormGroup).getRawValue();
        const failed = validations
          .filter(v => v.when && this.rulesEngine.evaluateRuleGroup(v.when, value))
          .map(v => v.message);
        return failed.length ? {crossField: failed} : null;
      });
    }

    return group;
  }

  addBlocksToGroup(blocks: FormBlock[], group: FormGroup): void {
    blocks.forEach(block => {
      if (block.type === 'group') {
        const gBlock = block as GroupBlock;
        const subGroup = new FormGroup({});
        this.addBlocksToGroup(gBlock.blocks, subGroup);
        group.addControl(gBlock.key, subGroup);
      } else if (block.type === 'list') {
        const lBlock = block as ListBlock;
        const array = new FormArray([] as FormGroup[]);
        // Seed the declared minimum so a list with minItems >= 1 opens on a usable row rather than an empty
        // state the employee has to discover the "add" button to escape.
        for (let i = 0; i < (lBlock.minItems ?? 0); i++) array.push(this.createListItem(lBlock));
        group.addControl(lBlock.key, array);
      } else if (block.type !== 'static-text') {
        const field = block as FieldBlock;
        group.addControl(field.key, new FormControl(this.defaultValue(field), this.validatorMapper.mapValidators(field)));
      }
    });
  }

  createListItem(listBlock: ListBlock): FormGroup {
    const group = new FormGroup({});
    this.addBlocksToGroup(listBlock.blocks, group);
    return group;
  }

  /**
   * Grows or shrinks every `list` block's {@link FormArray} to match the row count in `value`.
   *
   * This has to happen **before** `patchValue`: Angular's `FormArray.patchValue` only writes into controls that
   * already exist and silently ignores any extra rows in the incoming array. Without this, a submission with
   * three repeating rows would restore as zero rows and the data would look lost (#259).
   *
   * @param form the root form built by {@link buildFormFromDefinition}
   * @param definition the definition the form was built from
   * @param value the raw value about to be patched in (rows keyed by the list's key)
   */
  syncListRows(form: FormGroup, definition: FormDefinition, value: any): void {
    const walk = (blocks: FormBlock[], group: FormGroup, scope: any) => {
      for (const block of blocks) {
        if (block.type === 'group') {
          const gBlock = block as GroupBlock;
          const sub = group.get(gBlock.key);
          if (sub instanceof FormGroup) walk(gBlock.blocks, sub, scope?.[gBlock.key] ?? {});
        } else if (block.type === 'list') {
          const lBlock = block as ListBlock;
          const array = group.get(lBlock.key);
          if (!(array instanceof FormArray)) continue;

          const rows: unknown[] = Array.isArray(scope?.[lBlock.key]) ? scope[lBlock.key] : [];
          const wanted = Math.max(rows.length, lBlock.minItems ?? 0);
          while (array.length > wanted) array.removeAt(array.length - 1, {emitEvent: false});
          while (array.length < wanted) array.push(this.createListItem(lBlock), {emitEvent: false});

          // Recurse so a group nested inside a row is walked too.
          array.controls.forEach((row, index) => {
            if (row instanceof FormGroup) walk(lBlock.blocks, row, rows[index] ?? {});
          });
        }
      }
    };

    definition.pages.forEach(page => walk(page.blocks, form, value ?? {}));
  }

  /**
   * The starting value of a freshly built control: the field's declared default (#388), or the type's empty
   * value when it declares none — see {@link resolveDefault}.
   *
   * This is the *only* place a control's initial value is decided, and {@link addBlocksToGroup} is shared by the
   * root form, every nested `group` block, the `minItems` seed rows **and** {@link createListItem} — so this one
   * delegation covers a row the employee adds later just as much as the first render.
   */
  private defaultValue(field: FieldBlock): any {
    return resolveDefault(field);
  }
}
